import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import AuthField from "@/components/AuthField";
import AuthShell, { AUTH_BUTTON } from "@/components/AuthShell";
import GoogleAuthButton, { OrDivider } from "@/components/GoogleAuthButton";
import { trackSignUpCompleted } from "@/lib/analytics";
import { checkUsername } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { safeNext } from "@/lib/safe-next";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// Two-step signup: (1) fill the form to receive a 6-digit code; (2) enter the code to create the
// account and sign in. No account exists until the code is verified.
type Step = "form" | "verify";

// Seconds before another code can be requested (the backend keeps only one live code per email).
const RESEND_COOLDOWN = 30;

export default function RegisterPage() {
  useDocumentTitle("SkillPatch");
  const { register, verifySignup, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");

  const [step, setStep] = useState<Step>("form");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Live username availability (debounced) — the handle is the public profile link, so we check it
  // up front rather than only failing at submit.
  const [nameStatus, setNameStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [nameChecking, setNameChecking] = useState(false);
  // True right after the user types/pastes a disallowed character (we strip it and flash a hint).
  const [charError, setCharError] = useState(false);

  // Count the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Debounced availability check as the user types their handle.
  useEffect(() => {
    const raw = username.trim();
    if (!raw) { setNameStatus(null); setNameChecking(false); return; }
    let cancelled = false;
    setNameChecking(true); setNameStatus(null);
    const t = setTimeout(async () => {
      try {
        const r = await checkUsername(raw);
        if (!cancelled) setNameStatus({ ok: r.available, msg: r.available ? "Available" : (r.reason ?? "Unavailable") });
      } catch {
        if (!cancelled) setNameStatus(null);
      } finally {
        if (!cancelled) setNameChecking(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username]);

  // If an already-authenticated user reaches /register (e.g. Back button after signing
  // up/in), send them on to their destination rather than re-showing the signup form.
  if (!authLoading && user) return <Navigate to={safeNext(next)} replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const pending = await register(email, password, confirm, username.trim());
      // A local backend running with DEV_EXPOSE_OTP sends no mail and hands the code back here
      // instead — prefill it so signup can be exercised with no mail server. Undefined in any
      // normal deployment, which leaves the field empty exactly as before.
      setOtp(pending?.dev_otp ?? "");
      setStep("verify");
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verifySignup(email, otp.trim());
      // The account only exists once the code verifies — this is the first point at which a
      // sign-up has actually completed (form_start fires long before, and often never gets here).
      trackSignUpCompleted("email");
      // Replace (not push) so /register doesn't stay in history and the Back button
      // can't return the freshly-signed-in user to the signup form.
      navigate(safeNext(next), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't verify the code");
    } finally {
      setLoading(false);
    }
  }

  // Re-submit the same details to get a fresh code (the backend replaces the outstanding one).
  async function onResend() {
    if (cooldown > 0 || loading) return;
    setError(null);
    try {
      const again = await register(email, password, confirm, username.trim());
      setOtp(again?.dev_otp ?? "");
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend the code");
    }
  }

  const footer = (
    <>
      Already have an account?{" "}
      <Link
        to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`}
        className="font-medium text-accent hover:underline"
      >
        Log in
      </Link>
    </>
  );

  if (step === "verify") {
    return (
      <AuthShell
        title="Verify your email"
        subtitle={`Enter the 6-digit code sent to ${email}`}
        footer={footer}
      >
        <form onSubmit={onVerify} className="flex flex-col gap-5">
          <AuthField
            icon="key"
            type="text"
            required
            autoFocus
            name="otp"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={otp}
            onChange={(v) => setOtp(v.replace(/\D/g, ""))}
          />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading || otp.length < 6}
            className={`mt-1 ${AUTH_BUTTON} disabled:opacity-60`}
          >
            {loading ? "Verifying…" : "Verify & create account"}
          </button>
          <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
            Didn&apos;t receive the code?{" "}
            {cooldown > 0 ? (
              <span>Resend in {cooldown}s</span>
            ) : (
              <button type="button" onClick={onResend} className="font-medium text-accent hover:underline">
                Resend code
              </button>
            )}
          </p>
          <p className="text-center text-sm">
            <button
              type="button"
              onClick={() => {
                setStep("form");
                setError(null);
              }}
              className="font-medium text-accent hover:underline"
            >
              Use a different email
            </button>
          </p>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join SkillPatch — publish, install, and improve agentic skills."
      footer={footer}
    >
      <GoogleAuthButton next={next || "/home"} />
      <OrDivider />
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <div>
          <AuthField
            icon="user"
            type="text"
            required
            minLength={3}
            maxLength={30}
            autoComplete="username"
            placeholder="Username"
            value={username}
            onChange={(v) => {
              // Only letters, numbers, underscore and dot are valid handle characters. Strip
              // anything else the moment it's typed/pasted and flash a hint so the field can never
              // hold — or the availability check ever bless — a character that isn't allowed.
              const cleaned = v.replace(/[^A-Za-z0-9_.]/g, "");
              setCharError(cleaned !== v);
              setUsername(cleaned);
            }}
            invalid={charError || (!!nameStatus && !nameStatus.ok)}
          />
          {charError ? (
            <p className="mt-1.5 text-xs text-red-400">Only letters, numbers, dot and underscore are allowed.</p>
          ) : username.trim() ? (
            nameChecking ? (
              <p className="mt-1.5 text-xs" style={{ color: "var(--text-muted)" }}>Checking…</p>
            ) : nameStatus ? (
              <p className={`mt-1.5 text-xs ${nameStatus.ok ? "text-emerald-500" : "text-red-400"}`}>
                {nameStatus.msg}
              </p>
            ) : null
          ) : null}
        </div>
        <AuthField icon="mail" type="email" required placeholder="Email address" value={email} onChange={setEmail} />
        <AuthField icon="lock" type="password" required minLength={8} placeholder="Password (min 8 chars)" value={password} onChange={setPassword} />
        <AuthField
          icon="lock"
          type="password"
          required
          placeholder="Confirm password"
          value={confirm}
          onChange={setConfirm}
          invalid={!!confirm && confirm !== password}
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button type="submit" disabled={loading || nameChecking || !(nameStatus?.ok ?? false)} className={`mt-1 ${AUTH_BUTTON} disabled:opacity-60`}>
          {loading ? "Sending code…" : "Create account"}
          {!loading && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          )}
        </button>
      </form>
    </AuthShell>
  );
}
